/**
 * <ry-accordion>
 *
 * Collapsible accordion component.
 *
 * Minimal markup (recommended):
 * <ry-accordion>
 *   <ry-accordion-item title="Title" open>Content</ry-accordion-item>
 * </ry-accordion>
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 * This decouples behavior from visual design.
 */

import { RyElement } from '../core/ry-element.js';

interface AccordionItemEventDetail {
  item: HTMLElement;
}

export class RyAccordion extends RyElement {
  setup(): void {
    // Transform minimal markup to full structure
    this.#transformMinimalMarkup();

    // Bind click handlers to all triggers
    this.$$('[data-ry-target="trigger"]').forEach(trigger => {
      this.on(trigger, 'click', this.#handleTriggerClick);
    });

    // Keyboard navigation
    this.on(this, 'keydown', this.#handleKeydown);

    // Initialize ARIA
    this.#initAria();
  }

  #transformMinimalMarkup(): void {
    // Convert <ry-accordion-item> to full structure
    // data-ry-target for JS queries, class for CSS styling
    this.$$('ry-accordion-item').forEach((item) => {
      const title = item.getAttribute('title') ?? 'Untitled';
      const isOpen = item.hasAttribute('open');
      const content = item.innerHTML;

      item.outerHTML = `
        <div data-ry-target="item" class="ry-accordion__item" data-ry-state="${isOpen ? 'open' : 'closed'}">
          <button data-ry-target="trigger" class="ry-accordion__trigger" aria-expanded="${isOpen}">
            ${title}
            <span data-ry-target="icon" class="ry-accordion__icon">&#9660;</span>
          </button>
          <div data-ry-target="panel" class="ry-accordion__panel">
            <div class="ry-accordion__content">${content}</div>
          </div>
        </div>
      `;
    });
  }

  #initAria(): void {
    this.$$('[data-ry-target="item"]').forEach((item, index) => {
      const trigger = item.querySelector('[data-ry-target="trigger"]');
      const panel = item.querySelector('[data-ry-target="panel"]');

      if (!trigger || !panel) return;

      // Generate IDs if not present
      const triggerId = trigger.id || `ry-accordion-trigger-${this.id || ''}-${index}`;
      const panelId = panel.id || `ry-accordion-panel-${this.id || ''}-${index}`;

      trigger.id = triggerId;
      panel.id = panelId;

      trigger.setAttribute('aria-controls', panelId);
      panel.setAttribute('aria-labelledby', triggerId);
      panel.setAttribute('role', 'region');

      // Set initial state
      const isOpen = (item as HTMLElement).dataset.ryState === 'open';
      trigger.setAttribute('aria-expanded', String(isOpen));
    });
  }

  #handleTriggerClick = (event: Event): void => {
    const trigger = event.currentTarget as HTMLElement;
    const item = trigger.closest('[data-ry-target="item"]') as HTMLElement | null;

    if (!item) return;

    this.toggle(item);
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    const triggers = this.$$<HTMLElement>('[data-ry-target="trigger"]');
    const currentIndex = triggers.indexOf(document.activeElement as HTMLElement);

    if (currentIndex === -1) return;

    let newIndex: number;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newIndex = (currentIndex + 1) % triggers.length;
        triggers[newIndex]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        newIndex = (currentIndex - 1 + triggers.length) % triggers.length;
        triggers[newIndex]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        triggers[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        triggers[triggers.length - 1]?.focus();
        break;
    }
  };

  toggle(item: HTMLElement): void {
    const isOpen = item.dataset.ryState === 'open';

    if (isOpen) {
      this.close(item);
    } else {
      this.open(item);
    }
  }

  open(item: HTMLElement): void {
    // Close others if not multiple mode
    if (!this.hasAttribute('multiple')) {
      this.$$<HTMLElement>('[data-ry-target="item"][data-ry-state="open"]').forEach(openItem => {
        if (openItem !== item) {
          this.close(openItem);
        }
      });
    }

    const trigger = item.querySelector('[data-ry-target="trigger"]');

    item.dataset.ryState = 'open';
    trigger?.setAttribute('aria-expanded', 'true');

    this.emit<AccordionItemEventDetail>('open', { item });
  }

  close(item: HTMLElement): void {
    const trigger = item.querySelector('[data-ry-target="trigger"]');

    item.dataset.ryState = 'closed';
    trigger?.setAttribute('aria-expanded', 'false');

    this.emit<AccordionItemEventDetail>('close', { item });
  }

  openAll(): void {
    this.$$<HTMLElement>('[data-ry-target="item"]').forEach(item => this.open(item));
  }

  closeAll(): void {
    this.$$<HTMLElement>('[data-ry-target="item"]').forEach(item => this.close(item));
  }
}

customElements.define('ry-accordion', RyAccordion);

// Register placeholder for minimal markup
if (!customElements.get('ry-accordion-item')) {
  customElements.define('ry-accordion-item', class extends HTMLElement {});
}
